/**
 * Every timestamp this product shows is IST.
 *
 * The clients are in India, their agents are in India, and the times they compare
 * a conversation against are the ones on the wall behind them. The browser's own
 * zone looks identical on a laptop in Mumbai and is wrong everywhere else —
 * including on a laptop in Mumbai whose zone was never set — which is exactly the
 * bug that makes a support call impossible to reconstruct.
 *
 * This lives here rather than in a component because it was written three times
 * before it was written once, and the third copy was the one that got it wrong.
 */
export const IST = 'Asia/Kolkata';

/** "27 Aug, 04:15 pm". No year: within a working inbox nothing is that old. */
export function formatIst(iso: string | null | undefined, fallback = '—'): string {
  if (!iso) return fallback;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: IST, day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit', hour12: true,
  }).format(date);
}

/** The same, said out loud, for anywhere the zone is not already established. */
export function formatIstLabelled(iso: string | null | undefined, fallback = '—'): string {
  const value = formatIst(iso, fallback);
  return value === fallback ? value : `${value} IST`;
}

/** Date only — for a field whose value is a day, not a moment. */
export function formatIstDate(iso: string | null | undefined, fallback = '—'): string {
  if (!iso) return fallback;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: IST, day: 'numeric', month: 'short', year: 'numeric',
  }).format(date);
}

/**
 * The value a `datetime-local` input wants, for "now in IST" plus some minutes.
 */
export function istInputValue(offsetMinutes = 0): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: IST, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date(Date.now() + offsetMinutes * 60_000));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

/**
 * IST wall-clock back to a real instant.
 *
 * A datetime-local value carries no zone, so the browser would read it as local
 * time. India has no daylight saving, so the offset is a constant rather than
 * something that has to be looked up for the chosen date.
 */
export function istInputToUtcIso(value: string): string {
  return new Date(`${value}:00+05:30`).toISOString();
}

/* ---------------------------------------------------------------------------
   The inbox's own stamps.

   These existed as bare toLocaleTimeString/toLocaleDateString calls inside the
   thread and the conversation list from M1 until M10 — five of them — and every
   one rendered in the browser's zone. On a laptop set to UTC, an 11:46 am
   message reads 06:16 am, and an agent reading it back to a customer is simply
   wrong. Same bug, same shape, as the admin portal's when().
   --------------------------------------------------------------------------- */

/** "11:46 am" — the stamp on a bubble and on a list row from today. */
export function formatIstTime(iso: string | null | undefined, fallback = '—'): string {
  if (!iso) return fallback;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: IST, hour: '2-digit', minute: '2-digit', hour12: true,
  }).format(date);
}

/** "Mon" — a list row from the last week. */
export function formatIstWeekday(iso: string | null | undefined, fallback = '—'): string {
  if (!iso) return fallback;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-IN', { timeZone: IST, weekday: 'short' }).format(date);
}

/** "27 Aug" — a list row older than that. */
export function formatIstDayMonth(iso: string | null | undefined, fallback = '—'): string {
  if (!iso) return fallback;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: IST, day: '2-digit', month: 'short',
  }).format(date);
}

/** "27 August 2026" — the day pill between two groups in a thread. */
export function formatIstLongDate(iso: string | null | undefined, fallback = '—'): string {
  if (!iso) return fallback;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: IST, day: 'numeric', month: 'long', year: 'numeric',
  }).format(date);
}

/**
 * "2026-08-31" for an instant, in IST.
 *
 * Which day a message belongs to is an IST question. Grouping by the browser's
 * day puts everything sent after 5:30 am IST under the wrong heading anywhere
 * east of India, and everything before it under the wrong heading anywhere west.
 */
export function istDayKey(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: IST, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * "Good morning" / "Good afternoon" / "Good evening", by the IST clock.
 *
 * By IST and not by the browser's, for the same reason as everything above it: an
 * agent in Pune opening the app at 9pm on a laptop still set to UTC would be
 * wished good afternoon. Cut at 12 and 17, which is where Indian English puts
 * them — "evening" starts well before dark.
 */
export function istGreeting(now: Date = new Date()): string {
  const hour = Number(new Intl.DateTimeFormat('en-GB', {
    timeZone: IST, hour: '2-digit', hour12: false,
  }).format(now));

  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}
